import { Map } from 'immutable';

import { getCharactersCountError, getInstructionsError, validatePersonalization } from '@/utils/validations/personalization';
import { shapePersonalizationQuestionsForAPI } from '@/utils/product/shapeAttributes';
import { personalizationHasFilesUpload } from '@/utils/product/checkAttribute';
import { setPersonalizationErrors } from '../product/errors';
import { clearErrors, setErrors } from '@/utils/errors';
import { getSize } from '@/utils/iterable/getSize';

import { ENABLED, ERROR, OPERATIONS, PERSONALIZATION_ERRORS, PERSONALIZATION_TYPE, REQUIRED, VALUE } from '@/constants/product';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';
import { ETSY } from '@/constants/channels';

export function editPersonalization({ product: source, userFeatures, ...values }) {
  let errors = EMPTY.MAP;
  let product = source;
  const operations = [];
  const channel = product.get('channel');

  if (!userFeatures.get(FEATURE.ETSY_PERSONALIZATION_QUESTIONS)) {
    if (ENABLED in values) {
      const enabled = values[ENABLED];
      product = product.setIn([FORM.PERSONALIZATION, ENABLED], enabled);
      operations.push(
        Map({ type: OPERATIONS[channel].PERSONALIZATION.IS_PERSONALIZABLE_SET, value: enabled })
      );

      if (enabled) {
        errors = validatePersonalization({ channel, personalization: product.get(FORM.PERSONALIZATION), userFeatures });

        if (!errors.size) {
          product = clearErrors({
            errors: PERSONALIZATION_ERRORS,
            path: FORM.PERSONALIZATION,
            product,
          });
        }
      } else {
        product = clearErrors({
          errors: PERSONALIZATION_ERRORS,
          path: FORM.PERSONALIZATION,
          product,
        });
      }
    }

    if (REQUIRED in values) {
      const required = values[REQUIRED];
      product = product.setIn([FORM.PERSONALIZATION, REQUIRED], required);
      operations.push(
        Map({ type: OPERATIONS[channel].PERSONALIZATION.IS_REQUIRED_SET, value: required })
      );
    }

    if (ATTRIBUTE.CHAR_COUNT_MAX in values) {
      const charCountMax = values[ATTRIBUTE.CHAR_COUNT_MAX];
      const error = getCharactersCountError({ channel, value: charCountMax });
      product = product.setIn([FORM.PERSONALIZATION, ATTRIBUTE.CHAR_COUNT_MAX, VALUE], charCountMax);

      if (error) {
        errors = errors.set(ATTRIBUTE.CHAR_COUNT_MAX, error);
      } else {
        product = clearErrors({ errors: [ATTRIBUTE.CHAR_COUNT_MAX], path: FORM.PERSONALIZATION, product });
        operations.push(
          Map({ type: OPERATIONS[channel].PERSONALIZATION.CHAR_COUNT_MAX_SET, value: charCountMax })
        );
      }
    }

    if (ATTRIBUTE.INSTRUCTIONS in values) {
      const instructions = values[ATTRIBUTE.INSTRUCTIONS];
      const error = getInstructionsError({ channel, value: instructions });
      product = product.setIn([FORM.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS, VALUE], instructions);

      if (error) {
        errors = errors.set(ATTRIBUTE.INSTRUCTIONS, error);
      } else {
        product = clearErrors({ errors: [ATTRIBUTE.INSTRUCTIONS], path: FORM.PERSONALIZATION, product });
        operations.push(
          Map({ type: OPERATIONS[channel].PERSONALIZATION.INSTRUCTIONS_SET, value: instructions })
        );
      }
    }

    if (getSize(errors) > 0) {
      product = setErrors({ errors, path: FORM.PERSONALIZATION, product });
    }
  } else if (VALUE in values) {
    product = setPersonalizationErrors({
      product: product
        .setIn([FORM.PERSONALIZATION, VALUE], values[VALUE])
        .setIn([FORM.PERSONALIZATION, PERSONALIZATION_TYPE.FILES], personalizationHasFilesUpload(values[VALUE])),
      userFeatures,
    });

    if (!product.hasIn([FORM.PERSONALIZATION, ERROR])) {
      operations.push(
        Map({
          type: OPERATIONS[ETSY].PERSONALIZATION_QUESTIONS,
          value: shapePersonalizationQuestionsForAPI({ questions: product.getIn([FORM.PERSONALIZATION, VALUE]) }),
        }),
      );
    }
  }

  return { product, operations };
}
